import { useState, useRef } from 'react';
import axios from 'axios';
import { API } from '../utils';

export const useDealerSearch = (formData, setFormData) => {
  const [dealerSuggestions, setDealerSuggestions] = useState([]);
  const [osmSuggestions, setOsmSuggestions] = useState([]);
  const [showDealerDropdown, setShowDealerDropdown] = useState(false);
  const [selectedDealerLocations, setSelectedDealerLocations] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const dealerInputRef = useRef(null);
  const searchTimerRef = useRef(null);

  const searchDealers = async (query) => {
    if (query.length < 2) {
      setDealerSuggestions([]);
      setOsmSuggestions([]);
      setShowDealerDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      const [dbResults, registroResults, osmResults] = await Promise.all([
        axios.get(`${API}/appointments/dealers/search?q=${encodeURIComponent(query)}`)
          .then(res => res.data).catch(() => []),
        axios.get(`${API}/appointments/imprese/search?q=${encodeURIComponent(query)}`)
          .then(res => res.data).catch(() => ({ enabled: false, results: [] })),
        fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=it&addressdetails=1&limit=5`, {
          headers: { 'Accept-Language': 'it' }
        })
          .then(res => res.json())
          .then(data => data.map(item => ({
            display_name: item.display_name,
            name: item.name || item.display_name.split(',')[0],
            address: item.address?.road ? `${item.address.road}${item.address.house_number ? ' ' + item.address.house_number : ''}` : '',
            city: item.address?.city || item.address?.town || item.address?.village || item.address?.municipality || '',
            province: item.address?.county?.substring(0, 2).toUpperCase() || item.address?.state_district?.substring(0, 2).toUpperCase() || '',
            postcode: item.address?.postcode || '',
            type: item.type,
            category: item.class,
            source: 'openstreetmap'
          }))).catch(() => [])
      ]);

      setDealerSuggestions(dbResults);
      if (registroResults.enabled && registroResults.results?.length > 0) {
        setOsmSuggestions(registroResults.results);
      } else {
        setOsmSuggestions(osmResults);
      }
      setShowDealerDropdown(true);
    } catch (e) {
      console.error("Error searching:", e);
      setDealerSuggestions([]);
      setOsmSuggestions([]);
      setShowDealerDropdown(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleDealerSearchChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, client_name: value }));

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (value.length < 2) {
      setShowDealerDropdown(false);
      setDealerSuggestions([]);
      setOsmSuggestions([]);
      return;
    }

    setIsSearching(true);
    setShowDealerDropdown(true);
    searchTimerRef.current = setTimeout(() => searchDealers(value), 500);
  };

  const selectDealer = (dealer) => {
    setFormData(prev => ({
      ...prev,
      client_name: dealer.client_name,
      client_tax_id: dealer.client_tax_id || ""
    }));
    setShowDealerDropdown(false);

    if (dealer.locations && dealer.locations.length > 1) {
      setSelectedDealerLocations(dealer.locations);
      setShowLocationDropdown(true);
    } else if (dealer.locations && dealer.locations.length === 1) {
      const loc = dealer.locations[0];
      setFormData(prev => ({
        ...prev,
        client_name: dealer.client_name,
        client_tax_id: dealer.client_tax_id || "",
        address: loc.address || "",
        city: loc.city || "",
        province: loc.province || ""
      }));
    }
  };

  const selectOsmResult = (item) => {
    const isRegistroImprese = item.source === 'registro_imprese';
    setFormData(prev => ({
      ...prev,
      client_name: item.client_name || item.name || "",
      client_tax_id: isRegistroImprese ? (item.piva || item.cf || "") : prev.client_tax_id,
      address: item.address || "",
      city: item.city || "",
      province: item.province || ""
    }));
    setShowDealerDropdown(false);
    setOsmSuggestions([]);
  };

  const selectLocation = (location) => {
    setFormData(prev => ({
      ...prev,
      address: location.address || "",
      city: location.city || "",
      province: location.province || ""
    }));
    setShowLocationDropdown(false);
    setSelectedDealerLocations([]);
  };

  return {
    dealerSuggestions, osmSuggestions,
    showDealerDropdown, setShowDealerDropdown,
    selectedDealerLocations, showLocationDropdown,
    isSearching, dealerInputRef,
    handleDealerSearchChange, selectDealer, selectOsmResult, selectLocation,
  };
};
